/**
 * lib/offline/offlineSaleIntent.ts
 *
 * P0-1 · Fase 8B — Repositorio LOCAL de intenciones de venta offline.
 *
 * Alcance 8B: registrar/consultar/administrar intenciones en IndexedDB.
 * NO sincroniza, NO llama al servidor, NO genera folio/firma/total autoritativo.
 * El dispositivo solo produce una INTENCIÓN pendiente (jamás una venta confirmada).
 *
 * Decisiones cerradas aplicadas:
 *  - Solo efectivo (tarjeta y mixtos bloqueados).
 *  - Descuentos > límite propio bloqueados (authorization_ref debe ser null en 8B).
 *  - Ventana 8h.
 *  - No se genera folio local; referencia provisional "PENDIENTE · <6 chars>".
 *  - No se reatribuye user/store/shift.
 *
 * NUNCA se almacena: PIN, pin_hash, contraseña, JWT, refresh token, datos de
 * tarjeta, CVV ni firma HMAC. El shape de entrada es estricto y se rechazan
 * campos no permitidos.
 */

import { db, type OfflineSaleIntent, type OfflineSaleIntentStatus } from '@lib/dexie/schema'
import { getOrCreateDeviceId } from '@lib/device/deviceId'
import { supabase } from '@lib/supabase/client'

// ── Constantes ───────────────────────────────────────────────────────────────
export const OFFLINE_WINDOW_HOURS = 8 // 8B fijo (futuro configurable, máx 12)
const WINDOW_MS = OFFLINE_WINDOW_HOURS * 60 * 60 * 1000

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isUuid = (v: unknown): v is string => typeof v === 'string' && UUID_RE.test(v)
const isFiniteNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

// ── Contrato de entrada (estricto) ───────────────────────────────────────────
export interface OfflineCartLine {
  product_id: string
  quantity: number
  requested_discount: number
  // Identidad por unidad de un producto rastreado. Solo ids; el servidor los vuelve a validar al
  // sincronizar. Ausente en NONE/LOT.
  unit_ids?: string[]
}
export interface OfflineCashPayment {
  method: 'efectivo'
  amount: number
}

export interface CreateOfflineDraftInput {
  user_id: string
  store_id: string
  business_id: string
  shift_id: string
  occurred_at?: string // ISO; por defecto ahora
  cart_snapshot: OfflineCartLine[]
  requested_payments: OfflineCashPayment[]
  // authorization_ref NO se acepta en 8B (descuentos > límite bloqueados offline).
}

export type ValidationResult = { ok: true } | { ok: false; code: string; message: string }

const err = (code: string, message: string): ValidationResult => ({ ok: false, code, message })

// Campos económicos/sensibles que el cliente NUNCA puede declarar como autoritativos.
const FORBIDDEN_INPUT_KEYS = [
  'total',
  'subtotal',
  'tax',
  'server_total',
  'signature',
  'folio',
  'server_folio',
  'authorization_ref',
  'pin',
  'pin_hash',
  'password',
  'jwt',
  'token',
  'card',
  'cvv',
]

// ── Validación ───────────────────────────────────────────────────────────────
export function validateDraftInput(input: CreateOfflineDraftInput): ValidationResult {
  // Rechazar campos no permitidos (incluye total autoritativo del cliente).
  for (const k of Object.keys(input as unknown as Record<string, unknown>)) {
    if (FORBIDDEN_INPUT_KEYS.includes(k)) {
      return err('CAMPO_NO_PERMITIDO', `Campo no permitido en intención offline: ${k}`)
    }
  }

  if (
    !isUuid(input.user_id) ||
    !isUuid(input.store_id) ||
    !isUuid(input.business_id) ||
    !isUuid(input.shift_id)
  ) {
    return err('CONTEXTO_INCOMPLETO', 'user/store/business/shift son obligatorios y deben ser UUID')
  }

  if (!Array.isArray(input.cart_snapshot) || input.cart_snapshot.length === 0) {
    return err('CARRITO_VACIO', 'El carrito no puede estar vacío')
  }
  for (const line of input.cart_snapshot) {
    if (!isUuid(line?.product_id))
      return err('PRODUCTO_SIN_UUID', 'Cada línea requiere product_id (UUID)')
    if (!isFiniteNum(line.quantity) || line.quantity <= 0)
      return err('CANTIDAD_INVALIDA', 'quantity debe ser un número > 0')
    if (!isFiniteNum(line.requested_discount) || line.requested_discount < 0) {
      return err('DESCUENTO_NEGATIVO', 'requested_discount no puede ser negativo')
    }
  }

  if (!Array.isArray(input.requested_payments) || input.requested_payments.length === 0) {
    return err('PAGOS_VACIOS', 'Debe declararse al menos un pago')
  }
  if (input.requested_payments.length > 1) {
    return err('PAGO_MIXTO_NO_PERMITIDO', 'Pagos mixtos no permitidos offline en 8B')
  }
  for (const p of input.requested_payments) {
    if (p?.method !== 'efectivo')
      return err('METODO_NO_PERMITIDO', 'Solo efectivo permitido offline en 8B')
    if (!isFiniteNum(p.amount) || p.amount <= 0)
      return err('MONTO_INVALIDO', 'El monto debe ser un número > 0')
  }

  const occurred = input.occurred_at ?? new Date().toISOString()
  const occMs = Date.parse(occurred)
  if (!Number.isFinite(occMs)) return err('OCCURRED_AT_INVALIDO', 'occurred_at inválido')

  return { ok: true }
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function provisionalReference(localId: string): string {
  return `PENDIENTE · ${localId.slice(0, 6)}`
}

export interface OfflineContext {
  device_id?: string
  user_id: string
  store_id: string
  business_id: string
}

/** Filtra siempre por device+user+store+business (aislamiento estricto). */
async function contextGuardedCollection(ctx: OfflineContext) {
  const device_id = ctx.device_id ?? (await getOrCreateDeviceId())
  return db.offline_sale_intents
    .where('[device_id+user_id+store_id]')
    .equals([device_id, ctx.user_id, ctx.store_id])
    .and(i => i.business_id === ctx.business_id)
}

// ── API del repositorio ──────────────────────────────────────────────────────

/** Crea una intención en estado 'draft'. Genera ids/expires/referencia. */
export async function createOfflineSaleDraft(
  input: CreateOfflineDraftInput
): Promise<{ ok: true; intent: OfflineSaleIntent } | { ok: false; code: string; message: string }> {
  const validation = validateDraftInput(input)
  if (!validation.ok) return validation

  const device_id = await getOrCreateDeviceId()
  const local_id = crypto.randomUUID()
  const idempotency_key = crypto.randomUUID()
  const now = Date.now()
  const occurred_at = input.occurred_at ?? new Date().toISOString()
  const expires_at = new Date(Date.parse(occurred_at) + WINDOW_MS).toISOString()

  const intent: OfflineSaleIntent = {
    local_id,
    idempotency_key,
    device_id,
    user_id: input.user_id,
    store_id: input.store_id,
    business_id: input.business_id,
    shift_id: input.shift_id,
    occurred_at,
    cart_snapshot: input.cart_snapshot.map(l => ({
      product_id: l.product_id,
      quantity: l.quantity,
      requested_discount: l.requested_discount,
      ...(l.unit_ids && l.unit_ids.length > 0 ? { unit_ids: l.unit_ids } : {}),
    })),
    requested_payments: input.requested_payments.map(p => ({
      method: 'efectivo',
      amount: p.amount,
    })),
    authorization_ref: null,
    status: 'draft',
    attempts: 0,
    last_error_code: null,
    last_attempt_at: null,
    expires_at,
    server_sale_id: null,
    server_folio: null,
    server_total: null,
    provisional_reference: provisionalReference(local_id),
    created_at: now,
    updated_at: now,
  }

  try {
    await db.offline_sale_intents.add(intent) // '&idempotency_key' rechaza duplicados
  } catch {
    return { ok: false, code: 'DUPLICADO', message: 'idempotency_key/local_id duplicado' }
  }
  return { ok: true, intent }
}

/** Transición draft → queued (única permitida en 8B para "encolar"). */
export async function queueOfflineSaleIntent(localId: string): Promise<ValidationResult> {
  return db.transaction('rw', db.offline_sale_intents, async () => {
    const cur = await db.offline_sale_intents.get(localId)
    if (!cur) return err('NO_ENCONTRADA', 'Intención inexistente')
    if (cur.status !== 'draft')
      return err('TRANSICION_INVALIDA', `No se puede encolar desde ${cur.status}`)
    await db.offline_sale_intents.update(localId, { status: 'queued', updated_at: Date.now() })
    return { ok: true }
  })
}

/** Cancela una intención (solo draft/queued → cancelled). */
export async function cancelOfflineSaleIntent(localId: string): Promise<ValidationResult> {
  return db.transaction('rw', db.offline_sale_intents, async () => {
    const cur = await db.offline_sale_intents.get(localId)
    if (!cur) return err('NO_ENCONTRADA', 'Intención inexistente')
    if (cur.status !== 'draft' && cur.status !== 'queued') {
      return err('TRANSICION_INVALIDA', `No se puede cancelar desde ${cur.status}`)
    }
    await db.offline_sale_intents.update(localId, { status: 'cancelled', updated_at: Date.now() })
    return { ok: true }
  })
}

/** Lee una intención por id (sin filtro de contexto; para detalle). */
export async function getOfflineSaleIntent(
  localId: string
): Promise<OfflineSaleIntent | undefined> {
  return db.offline_sale_intents.get(localId)
}

/** Lista intenciones del contexto, ordenadas por occurred_at desc. */
export async function listOfflineSaleIntents(
  ctx: OfflineContext,
  opts?: { statuses?: OfflineSaleIntentStatus[] }
): Promise<OfflineSaleIntent[]> {
  const coll = await contextGuardedCollection(ctx)
  let rows = await coll.toArray()
  if (opts?.statuses && opts.statuses.length > 0) {
    const set = new Set(opts.statuses)
    rows = rows.filter(i => set.has(i.status))
  }
  return rows.sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))
}

/** Intenciones "pendientes" (draft/queued) del contexto. */
export async function getPendingIntentsForContext(
  ctx: OfflineContext
): Promise<OfflineSaleIntent[]> {
  return listOfflineSaleIntents(ctx, { statuses: ['draft', 'queued'] })
}

/** Conteo de intenciones pendientes (draft/queued) del contexto. */
export async function countPendingOfflineIntents(ctx: OfflineContext): Promise<number> {
  const rows = await getPendingIntentsForContext(ctx)
  return rows.length
}

// ── Ciclo de vida de sincronización (Fase 8C) ────────────────────────────────

/** Intenciones 'queued' del contexto, en orden FIFO por occurred_at (asc). */
export async function getQueuedIntentsForContext(
  ctx: OfflineContext
): Promise<OfflineSaleIntent[]> {
  const rows = await listOfflineSaleIntents(ctx, { statuses: ['queued'] })
  return rows.sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at))
}

/** Reclama una intención para envío: queued → syncing (atómico). Devuelve false si ya no está queued. */
export async function claimIntentForSync(localId: string): Promise<boolean> {
  return db.transaction('rw', db.offline_sale_intents, async () => {
    const cur = await db.offline_sale_intents.get(localId)
    if (!cur || cur.status !== 'queued') return false
    await db.offline_sale_intents.update(localId, {
      status: 'syncing',
      last_attempt_at: Date.now(),
      updated_at: Date.now(),
    })
    return true
  })
}

export interface ConfirmedServerData {
  server_sale_id: string
  server_folio: string
  server_total: number
  server_paid: number
  server_change: number
  signature_status: string
}

/** Marca confirmada con datos autoritativos del servidor. */
export async function markIntentConfirmed(
  localId: string,
  data: ConfirmedServerData
): Promise<void> {
  await db.offline_sale_intents.update(localId, {
    status: 'confirmed',
    server_sale_id: data.server_sale_id,
    server_folio: data.server_folio,
    server_total: data.server_total,
    server_paid: data.server_paid,
    server_change: data.server_change,
    signature_status: data.signature_status,
    conflict_code: null,
    last_error_code: null,
    confirmed_at: Date.now(),
    updated_at: Date.now(),
  })
}

/** Marca conflicto (requiere atención humana en 8D). */
export async function markIntentConflict(localId: string, code: string): Promise<void> {
  await db.offline_sale_intents.update(localId, {
    status: 'conflict',
    conflict_code: code,
    last_error_code: code,
    updated_at: Date.now(),
  })
}

/** Marca rechazo definitivo. */
export async function markIntentRejected(localId: string, code: string): Promise<void> {
  await db.offline_sale_intents.update(localId, {
    status: 'rejected',
    last_error_code: code,
    updated_at: Date.now(),
  })
}

/** Devuelve a la cola (error recuperable): SOLO syncing → queued, attempts++.
 *  Nunca reactiva rejected/expired/conflict (evita mover arbitrariamente a queued). */
export async function requeueIntent(localId: string, code: string): Promise<void> {
  await db.transaction('rw', db.offline_sale_intents, async () => {
    const cur = await db.offline_sale_intents.get(localId)
    if (!cur || cur.status !== 'syncing') return
    await db.offline_sale_intents.update(localId, {
      status: 'queued',
      attempts: (cur.attempts ?? 0) + 1,
      last_error_code: code,
      updated_at: Date.now(),
    })
  })
}

/** Marca expirada (ventana vencida antes de enviar). */
export async function markIntentExpired(localId: string): Promise<void> {
  await db.offline_sale_intents.update(localId, {
    status: 'expired',
    last_error_code: 'VENTANA_VENCIDA',
    updated_at: Date.now(),
  })
}

/** ¿La intención está vencida respecto a su ventana? */
export function isIntentExpired(intent: OfflineSaleIntent, nowMs: number = Date.now()): boolean {
  return nowMs > Date.parse(intent.expires_at)
}

// ── Ticket final desde una venta confirmada (Fase 8G) ────────────────────────
export interface FinalTicket {
  saleId: string
  folio: string
  // Número de ticket (00230): autoridad visible de la venta. get_sale_ticket devuelve el bigint
  // (number|string según serialización de Supabase) y su representación de 10 dígitos. La
  // presentación se deriva SIEMPRE con formatTicketNumber; el folio queda como referencia legada.
  ticketNumber?: number | string | null
  ticketNumberFormatted?: string | null
  subtotal: number
  discount: number
  tax: number
  total: number
  paid: number
  change: number
  createdAt: string
  signatureStatus: string
  sellerName: string
  storeCode: string
  clientName: string | null
  items: Array<{ name: string; qty: number; unitPrice: number; discount: number; total: number }>
  payments: Array<{ method: string; amount: number }>
}

/** Lee el ticket final (server-authoritative) por sale_id. NO recalcula localmente. */
export async function fetchSaleTicket(
  saleId: string
): Promise<{ ok: true; ticket: FinalTicket } | { ok: false; code: string; message: string }> {
  const { data, error } = await supabase.rpc('get_sale_ticket', { p_sale_id: saleId })
  if (error) {
    const raw = error.message ?? 'Error al leer ticket'
    return { ok: false, code: raw.split(':')[0]?.trim() ?? 'ERROR', message: raw }
  }
  if (!data || !(data as { saleId?: string }).saleId) {
    return { ok: false, code: 'RESPUESTA_INVALIDA', message: 'Ticket no disponible' }
  }
  return { ok: true, ticket: data as FinalTicket }
}

// ── Métricas locales de la cola offline (Fase 8G) ────────────────────────────
export interface OfflineMetrics {
  total: number
  queued: number
  syncing: number
  conflict: number
  confirmed: number
  expired: number
  rejected: number
  oldestQueuedAgeMs: number | null
}

/** Conteo de intenciones que deben advertirse antes de logout/cambio de contexto
 *  (draft/queued/syncing/conflict). Aislado por device+user+store+business. */
export async function countPendingForGuard(
  ctx: OfflineContext
): Promise<{ count: number; oldestAgeMs: number | null }> {
  const rows = await listOfflineSaleIntents(ctx, {
    statuses: ['draft', 'queued', 'syncing', 'conflict'],
  })
  if (rows.length === 0) return { count: 0, oldestAgeMs: null }
  const now = Date.now()
  const oldest = Math.min(...rows.map(r => Date.parse(r.occurred_at)))
  return { count: rows.length, oldestAgeMs: now - oldest }
}

/** Métricas LOCALES (no se envían a ningún servicio remoto). Sin PIN/JWT/HMAC. */
export async function getOfflineMetrics(ctx: OfflineContext): Promise<OfflineMetrics> {
  const rows = await listOfflineSaleIntents(ctx)
  const now = Date.now()
  const count = (st: OfflineSaleIntentStatus) => rows.filter(r => r.status === st).length
  const queuedTimes = rows.filter(r => r.status === 'queued').map(r => Date.parse(r.occurred_at))
  return {
    total: rows.length,
    queued: count('queued'),
    syncing: count('syncing'),
    conflict: count('conflict'),
    confirmed: count('confirmed'),
    expired: count('expired'),
    rejected: count('rejected'),
    oldestQueuedAgeMs: queuedTimes.length ? now - Math.min(...queuedTimes) : null,
  }
}

// ── Cache del límite de descuento del vendedor (Fase 8E) ─────────────────────
// Se cachea cuando hay conexión (my_discount_limit) para poder VALIDAR offline
// que un descuento cabe en el límite propio. El servidor revalida al sincronizar.
export async function cacheDiscountLimit(
  userId: string,
  storeId: string,
  maxPercent: number
): Promise<void> {
  await db.app_meta.put({
    key: `disc_limit:${userId}:${storeId}`,
    value: String(maxPercent),
    updated_at: Date.now(),
  })
}

export async function readCachedDiscountLimit(
  userId: string,
  storeId: string
): Promise<number | null> {
  const row = await db.app_meta.get(`disc_limit:${userId}:${storeId}`)
  if (!row) return null
  const n = Number(row.value)
  return Number.isFinite(n) ? n : null
}

// ── Resolución de conflictos (Fase 8D) ───────────────────────────────────────
export type OfflineResolutionType =
  | 'review_started'
  | 'escalated'
  | 'resolved_cancelled'
  | 'resolved_adjustment'
  | 'cash_reconciled'

const RESOLUTION_TO_STATUS: Record<OfflineResolutionType, OfflineSaleIntentStatus | null> = {
  review_started: 'under_review',
  escalated: 'escalated',
  resolved_cancelled: 'resolved_cancelled',
  resolved_adjustment: 'resolved_adjustment',
  cash_reconciled: null, // no cambia el estado; solo concilia efectivo
}

// Estados que admiten revisión/resolución humana.
const REVIEWABLE = new Set<OfflineSaleIntentStatus>([
  'conflict',
  'under_review',
  'escalated',
  'rejected',
  'expired',
])

/**
 * Registra una resolución server-side (RPC autoritativo) y refleja el resultado en
 * el intent local. NO modifica carrito/user/store/turno, NO crea venta, NO toca
 * stock/caja. El servidor valida rol/alcance/transición (no se confía en el frontend).
 */
export async function resolveOfflineConflict(
  intent: OfflineSaleIntent,
  params: {
    resolutionType: OfflineResolutionType
    reason: string
    notes?: string
    cashDeclared?: number
    cashStatus?: string
  }
): Promise<{ ok: true; resolutionId: string } | { ok: false; code: string; message: string }> {
  if (!REVIEWABLE.has(intent.status)) {
    return {
      ok: false,
      code: 'ESTADO_NO_REVISABLE',
      message: `No se puede resolver una intención en estado ${intent.status}`,
    }
  }
  if (!params.reason || params.reason.trim().length < 3) {
    return { ok: false, code: 'MOTIVO_REQUERIDO', message: 'El motivo es obligatorio' }
  }

  const { data, error } = await supabase.rpc('create_offline_intent_resolution', {
    p_local_intent_id: intent.local_id,
    p_device_id: intent.device_id,
    p_idempotency_key: intent.idempotency_key,
    p_store_id: intent.store_id,
    p_business_id: intent.business_id,
    p_seller_user_id: intent.user_id,
    p_occurred_at: intent.occurred_at,
    p_conflict_code: intent.conflict_code ?? intent.last_error_code ?? 'DESCONOCIDO',
    p_resolution_type: params.resolutionType,
    p_reason: params.reason.trim(),
    p_notes: params.notes ?? null,
    p_cash_received_declared: params.cashDeclared ?? null,
    p_cash_reconciliation_status: params.cashStatus ?? 'none',
  })
  if (error) {
    const raw = error.message ?? 'Error de resolución'
    return { ok: false, code: raw.split(':')[0]?.trim() ?? 'ERROR', message: raw }
  }
  const resolutionId = (data as { resolutionId?: string })?.resolutionId
  if (!resolutionId) return { ok: false, code: 'RESPUESTA_INVALIDA', message: 'Respuesta inválida' }

  const newStatus = RESOLUTION_TO_STATUS[params.resolutionType]
  const patch: Partial<OfflineSaleIntent> = {
    resolution_id: resolutionId,
    resolution_type: params.resolutionType,
    updated_at: Date.now(),
  }
  if (newStatus) {
    patch.status = newStatus
    patch.resolved_at = Date.now()
  }
  if (params.cashDeclared != null) patch.cash_received_declared = params.cashDeclared
  if (params.cashStatus) patch.cash_reconciliation_status = params.cashStatus
  await db.offline_sale_intents.update(intent.local_id, patch)
  return { ok: true, resolutionId }
}
